import { signal } from '@preact/signals';
import { getSWRegistration } from './register-sw';
import { fetchVapidKey, subscribePush as apiSubscribe, unsubscribePush as apiUnsubscribe } from '@/api';

/**
 * Клиентский стор Web Push подписки.
 *
 * Поддержка ограничена: нужен Service Worker + PushManager + Notification.
 * На iOS push работает только в iOS 16.4+ и только в установленном PWA (standalone).
 */

export const pushSupported = signal(detectSupport());
export const pushPermission = signal(typeof Notification !== 'undefined' ? Notification.permission : 'default');
export const pushSubscribed = signal(false);
export const pushBusy = signal(false);
export const pushError = signal('');

function detectSupport() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** base64url → Uint8Array (формат applicationServerKey). Экспортирован для тестов. */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/** Определяет текущее состояние подписки (вызывать после логина). */
export async function initPush() {
  if (!pushSupported.value) return;
  pushPermission.value = Notification.permission;
  try {
    const reg = await getSWRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    pushSubscribed.value = !!sub;
  } catch {
    /* нет SW / нет доступа — оставляем как есть */
  }
}

/**
 * Подписаться на push. Запрашивает разрешение, создаёт подписку, шлёт на сервер.
 * Возвращает true при успехе.
 */
export async function enablePush() {
  pushError.value = '';
  if (!pushSupported.value) {
    pushError.value = 'Уведомления не поддерживаются этим браузером';
    return false;
  }
  pushBusy.value = true;
  try {
    const permission = await Notification.requestPermission();
    pushPermission.value = permission;
    if (permission !== 'granted') {
      pushError.value =
        permission === 'denied'
          ? 'Уведомления заблокированы. Разрешите их в настройках браузера.'
          : 'Разрешение на уведомления не получено';
      return false;
    }

    const reg = await getSWRegistration();
    if (!reg) {
      pushError.value = 'Service worker не готов';
      return false;
    }

    const { public_key: vapidKey } = await fetchVapidKey();
    if (!vapidKey) {
      pushError.value = 'Сервер не настроен для уведомлений';
      return false;
    }

    // Переиспользуем существующую подписку или создаём новую.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const json = sub.toJSON();
    await apiSubscribe({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });

    pushSubscribed.value = true;
    return true;
  } catch (e) {
    pushError.value = e?.message || 'Не удалось включить уведомления';
    return false;
  } finally {
    pushBusy.value = false;
  }
}

/** Отписаться от push (на этом устройстве). */
export async function disablePush() {
  pushError.value = '';
  pushBusy.value = true;
  try {
    const reg = await getSWRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await apiUnsubscribe(endpoint);
        } catch {
          /* сервер мог уже удалить — не критично */
        }
      }
    }
    pushSubscribed.value = false;
    return true;
  } catch (e) {
    pushError.value = e?.message || 'Не удалось отключить уведомления';
    return false;
  } finally {
    pushBusy.value = false;
  }
}
